// DieView: a single 2D die rendered entirely with PixiJS Graphics (no external
// or remote assets). A rounded translucent-glass body with neon edge, readable
// pips, a soft drop shadow, and a glow halo used for the "match" state. The view
// is purely a renderer: it exposes setFace() and applyFrame() and contains NO
// game logic. Pip layouts for 1..6 are the standard western die arrangement.
//
// The die is drawn around its own local origin (0,0 = center) so rotation and
// squash-and-stretch scale about the center, which is what the animator expects.
import { Container, Graphics } from 'pixi.js';
import type { DieFrame, HighlightState } from './dice-animator.js';

// Pip positions as fractions of half-size, on a 3x3 grid (cols/rows: -1,0,1).
const PIPS: Record<number, ReadonlyArray<readonly [number, number]>> = {
  1: [[0, 0]],
  2: [
    [-1, -1],
    [1, 1],
  ],
  3: [
    [-1, -1],
    [0, 0],
    [1, 1],
  ],
  4: [
    [-1, -1],
    [1, -1],
    [-1, 1],
    [1, 1],
  ],
  5: [
    [-1, -1],
    [1, -1],
    [0, 0],
    [-1, 1],
    [1, 1],
  ],
  6: [
    [-1, -1],
    [1, -1],
    [-1, 0],
    [1, 0],
    [-1, 1],
    [1, 1],
  ],
};

const COLORS = {
  bodyTop: 0x1b3a78,
  bodyBottom: 0x0d1f4d,
  edge: 0x6ee2ff,
  edgeMatch: 0x7dffc0,
  edgeDim: 0x33518f,
  pip: 0xeaf4ff,
  pipMatch: 0xffffff,
  pipDim: 0x9fb6e0,
  glowMatch: 0x38e8a0,
  shadow: 0x000000,
} as const;

export class DieView extends Container {
  private readonly glow = new Graphics();
  private readonly shadow = new Graphics();
  private readonly body = new Graphics();
  private readonly pips = new Graphics();
  private readonly size: number;
  private readonly half: number;
  private readonly pad: number;
  private baseX = 0;
  private baseY = 0;
  private face = 1;
  private highlight: HighlightState = 'none';

  constructor(size: number) {
    super();
    this.size = size;
    this.half = size / 2;
    this.pad = size * 0.26; // pip inset from the body edge
    // z-order: shadow (lowest), glow halo, body, pips.
    this.addChild(this.shadow, this.glow, this.body, this.pips);
    this.drawShadow();
    this.drawBody('none');
    this.drawPips(1, 'none');
  }

  /** Pixels wide/tall of the die body (for layout sizing). */
  dieSize(): number {
    return this.size;
  }

  /** Sets the resting center position; applyFrame() offsets relative to this. */
  setBase(x: number, y: number): void {
    this.baseX = x;
    this.baseY = y;
    this.position.set(x, y);
  }

  /** Immediately shows a face with no transform (used for instant reveal). */
  setFace(value: number): void {
    const v = value >= 1 && value <= 6 ? value : 1;
    this.face = v;
    this.drawPips(v, this.highlight);
  }

  setHighlight(state: HighlightState): void {
    if (state === this.highlight) return;
    this.highlight = state;
    this.drawBody(state);
    this.drawPips(this.face, state);
  }

  /** Applies one animation frame (relative transform + visual state). */
  applyFrame(frame: DieFrame): void {
    if (frame.face !== this.face) {
      this.face = frame.face;
      this.drawPips(this.face, frame.highlight);
    }
    if (frame.highlight !== this.highlight) {
      this.highlight = frame.highlight;
      this.drawBody(frame.highlight);
      this.drawPips(this.face, frame.highlight);
    }
    this.rotation = frame.rotation;
    this.scale.set(frame.scaleX, frame.scaleY);
    this.skew.x = frame.skewX;
    this.position.set(this.baseX + frame.offsetX, this.baseY + frame.offsetY);
    // Match glow halo; also used subtly for the anticipation pulse.
    this.glow.alpha = Math.max(0, Math.min(1, frame.glow));
    // Motion "blur" approximation that stays strict-CSP + GPU friendly: fade the
    // body slightly while tumbling instead of running a per-die shader filter.
    this.body.alpha = 1 - 0.25 * Math.max(0, Math.min(1, frame.blur));
    this.pips.alpha = this.body.alpha;
    // Shadow compresses on impact; it does not rotate with the die.
    this.shadow.rotation = -this.rotation;
    this.shadow.scale.set(Math.max(0.2, frame.shadow), Math.max(0.2, frame.shadow) * 0.5);
  }

  private drawShadow(): void {
    const g = this.shadow;
    g.clear();
    g.ellipse(0, this.half + this.size * 0.16, this.half * 0.92, this.half * 0.34).fill({
      color: COLORS.shadow,
      alpha: 0.34,
    });
  }

  private drawBody(state: HighlightState): void {
    const g = this.body;
    g.clear();
    const r = this.size * 0.22;
    const edge = state === 'match' ? COLORS.edgeMatch : state === 'dim' ? COLORS.edgeDim : COLORS.edge;
    // Glass body: two stacked rounded rects fake a top->bottom gradient.
    g.roundRect(-this.half, -this.half, this.size, this.size, r).fill({ color: COLORS.bodyBottom, alpha: 0.96 });
    g.roundRect(-this.half, -this.half, this.size, this.size * 0.62, r).fill({ color: COLORS.bodyTop, alpha: 0.92 });
    // Neon edge.
    g.roundRect(-this.half, -this.half, this.size, this.size, r).stroke({
      width: Math.max(2, this.size * 0.04),
      color: edge,
      alpha: state === 'dim' ? 0.6 : 1,
    });
    // Soft top reflection highlight.
    g.roundRect(-this.half * 0.7, -this.half * 0.78, this.size * 0.7, this.size * 0.2, r * 0.6).fill({
      color: 0xffffff,
      alpha: 0.14,
    });

    // Match halo (drawn into the separate glow layer so alpha can be animated).
    const halo = this.glow;
    halo.clear();
    halo.roundRect(-this.half * 1.18, -this.half * 1.18, this.size * 1.18, this.size * 1.18, r * 1.3).fill({
      color: state === 'match' ? COLORS.glowMatch : COLORS.edge,
      alpha: 0.5,
    });
    halo.alpha = 0;
  }

  private drawPips(value: number, state: HighlightState): void {
    const g = this.pips;
    g.clear();
    const color = state === 'match' ? COLORS.pipMatch : state === 'dim' ? COLORS.pipDim : COLORS.pip;
    const pr = this.size * 0.085;
    const spread = this.half - this.pad;
    for (const [cx, cy] of PIPS[value] ?? PIPS[1]!) {
      g.circle(cx * spread, cy * spread, pr).fill({ color, alpha: state === 'dim' ? 0.7 : 1 });
    }
  }
}
